import cn from 'classnames';
import { useMemo } from 'react';
import type { ClassNames } from 'react-day-picker';
import { getDefaultClassNames } from 'react-day-picker';

const defaultClassNames = getDefaultClassNames();

export function useDateRangeClassNames(
  classNames?: Partial<ClassNames>,
): Partial<ClassNames> {
  const baseClassNames = useMemo(
    () =>
      ({
        disabled: cn(
          `${defaultClassNames.disabled} [&>button]:!text-text/disabled`,
        ),
        caption_label: cn(
          `${defaultClassNames.caption_label} flex items-center gap-spacing/gap/soft-4px text-text/primary mhg-alpha-typography-subheading-2`,
        ),
        month_caption: cn(
          `${defaultClassNames.caption_label} flex justify-center items-center !h-[--button-size-regular]`,
        ),
        chevron: cn(`${defaultClassNames.chevron} !fill-text/primary`),
        month: cn(`${defaultClassNames.month} grid gap-y-spacing/gap/soft-8px`),
        months: cn(
          `${defaultClassNames.months} flex items-start flex-nowrap gap-spacing/padding/section-x-lg`,
        ),
        month_grid: cn(
          `${defaultClassNames.month_grid} grid gap-y-spacing/gap/soft-8px`,
        ),
        weekdays: cn(`${defaultClassNames.weekdays} grid grid-cols-7`),
        weekday: cn(`${defaultClassNames.weekday} flex justify-center`),
        day_button: cn(
          `${defaultClassNames.day_button} relative !size-[--button-size-regular] rounded-full p-0 text-center !text-text/primary !alpha-bullet-2 focus-within:relative hover:rounded-full hover:border-2 hover:border-primary-800 disabled:!cursor-not-allowed disabled:hover:border-0`,
        ),
        selected: cn(
          `${defaultClassNames.selected} !font-normal !font-sans !text-[14px] hover:[&>button]:!border-2`,
        ),
        weeks: cn(`${defaultClassNames.weeks} grid gap-y-spacing/gap/soft-8px`),
        week: cn(`${defaultClassNames.week} grid grid-cols-7`),
        day: cn(
          `${defaultClassNames.day} box-content flex items-center justify-center !h-[--button-size-regular] px-spacing/gap/soft-4px !alpha-form-input`,
        ),
        range_start: cn(
          `${defaultClassNames.range_start} disabled:!bg-white [&>button]:!border-primary-800 [&>button]:!bg-primary-800 [&>button]:!text-white disabled:[&>button]:!bg-gray-800`,
          'rdp-range-start',
        ),
        range_end: cn(
          `${defaultClassNames.range_end} disabled:!bg-white [&>button]:!border [&>button]:!border-borders/form-default [&>button]:!bg-[--buttons-base] [&>button]:!text-text/primary hover:[&>button]:border-borders/form-hover disabled:[&>button]:!bg-gray-800`,
          'rdp-range-end',
        ),
        range_middle: cn(
          `!bg-[--fill-primary-light] [&>button]:!border-0 [&>button]:!text-text/primary`,
          'rdp-range-middle',
        ),
      }) satisfies Partial<ClassNames>,
    [],
  );

  return useMemo(() => {
    if (!classNames) {
      return baseClassNames;
    }

    const merged: Record<string, string> = { ...baseClassNames };

    (
      Object.entries(classNames) as Array<
        [keyof ClassNames, string | undefined]
      >
    ).forEach(([key, value]) => {
      if (!value) {
        return;
      }

      const baseValue = (baseClassNames as unknown as Record<string, string>)[
        key
      ];
      merged[key] = baseValue ? cn(baseValue, value) : value;
    });

    return merged;
  }, [baseClassNames, classNames]);
}
